var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useRef, useContext } from 'react';
import Raphael from 'raphael';
import globalStore from '../store';
import { DataContext } from '../player/DataContext';
import '../styles/whiteboard.scss';
import { selectTool, isMultiSelectModifierHeld } from '../helper/helper';
import customEventHandler from '../helper/customEvents';
import { saveState } from '../helper/stateManagerHelper';
import { registerMultiSelectSaveEntries } from '../helper/multiSelectSaveCoordinator';
import { announce } from '../helper/announcer';
import { t } from 'i18next';
import { pendingDuplicateIds } from '../helper/hooks/useDuplicateAnnounce';
var RaphaelCanvas = function (props) {
    var _a = globalStore.useStoreState(function (store) { return store.playerStore; }), zoomLevel = _a.zoomLevel, canvasHeight = _a.canvasHeight, canvasWidth = _a.canvasWidth;
    var _b = useContext(DataContext), annotations = _b.annotations, selectedTool = _b.selectedTool, setSelectedTool = _b.setSelectedTool, setAnnotations = _b.setAnnotations, color = _b.color, thickness = _b.thickness, setContextMenu = _b.setContextMenu;
    var dispatch = props.dispatch;
    var containerRef = useRef(null);
    var paperRef = useRef(null);
    var selectedToolRef = useRef(selectedTool);
    selectedToolRef.current = selectedTool;
    var annotationsRef = useRef(annotations);
    annotationsRef.current = annotations;
    // Dedup stacked Raphaël hit-targets: a single click can fire multiple
    // `.click(...)` handlers (e.g. transparent patch + visible element, or
    // arrowhead + line segment). Without dedup, the first call toggles the
    // annotation in and the second toggles it back out. Track which native
    // MouseEvent we've already processed and skip re-entries for it.
    var handledClickEvents = useRef(new WeakSet());
    var handleAnnotationClick = function (e, _ind) {
        var nativeEvt = ((e === null || e === void 0 ? void 0 : e.originalEvent) || e);
        if (handledClickEvents.current.has(nativeEvt))
            return;
        handledClickEvents.current.add(nativeEvt);
        if (dispatch)
            dispatch("annotations_".concat(_ind));
        var next = selectTool(nativeEvt, "annotations_".concat(_ind), selectedToolRef.current);
        // Sync ref before the focus listener fires (the SVG element gets focus
        // immediately after click, and that listener would otherwise see a
        // stale ref and collapse a shift-multi-select).
        selectedToolRef.current = next;
        setSelectedTool(next);
    };
    var dragStateRef = useRef(null);
    var handleAnnotationMouseDown = function (nativeEvt, idx) {
        var _a;
        var ann = annotationsRef.current[idx];
        if (!ann || ((_a = ann.data.lock) === null || _a === void 0 ? void 0 : _a.positioning))
            return;
        dragStateRef.current = {
            idx: idx,
            startClientX: nativeEvt.clientX,
            startClientY: nativeEvt.clientY,
            startPoints: ann.type === 'line' && ann.data.points ? ann.data.points.map(function (p) { return (__assign({}, p)); }) : undefined,
            startCenter: ann.data.center ? __assign({}, ann.data.center) : undefined,
            oldAnnJson: JSON.stringify(ann),
            hasMoved: false,
        };
        var sourceId = "annotations_".concat(idx);
        if (selectedToolRef.current.length > 1 && selectedToolRef.current.includes(sourceId)) {
            customEventHandler.dispatchEvent('multiSelectMoveStart', { sourceId: sourceId });
        }
    };
    useEffect(function () {
        var onWindowMouseMove = function (e) {
            var drag = dragStateRef.current;
            if (!drag)
                return;
            var dx = (e.clientX - drag.startClientX) / zoomLevel;
            var dy = (e.clientY - drag.startClientY) / zoomLevel;
            if (!drag.hasMoved && Math.sqrt(dx * dx + dy * dy) > 3)
                drag.hasMoved = true;
            if (!drag.hasMoved)
                return;
            var ann = annotationsRef.current[drag.idx];
            if (!ann)
                return;
            var newAnn;
            if (drag.startPoints) {
                newAnn = __assign(__assign({}, ann), { data: __assign(__assign({}, ann.data), { points: drag.startPoints.map(function (p) { return ({ x: p.x + dx, y: p.y + dy }); }) }) });
            }
            else if (drag.startCenter) {
                newAnn = __assign(__assign({}, ann), { data: __assign(__assign({}, ann.data), { center: { x: drag.startCenter.x + dx, y: drag.startCenter.y + dy } }) });
            }
            if (!newAnn)
                return;
            setAnnotations(function (prev) {
                var next = __spreadArray([], prev, true);
                next[drag.idx] = newAnn;
                return next;
            });
            var sourceId = "annotations_".concat(drag.idx);
            if (selectedToolRef.current.length > 1 && selectedToolRef.current.includes(sourceId)) {
                customEventHandler.dispatchEvent('multiSelectMove', { sourceId: sourceId, deltaX: dx, deltaY: dy });
            }
        };
        var onWindowMouseUp = function () {
            var drag = dragStateRef.current;
            if (!drag)
                return;
            if (drag.hasMoved) {
                var ann = annotationsRef.current[drag.idx];
                var sourceId = "annotations_".concat(drag.idx);
                if (ann) {
                    var entry = {
                        type: 'annotations',
                        id: String(drag.idx),
                        propertyName: 'completeData',
                        old: drag.oldAnnJson,
                        new: JSON.stringify(ann),
                    };
                    if (!registerMultiSelectSaveEntries(sourceId, [entry])) {
                        saveState(entry);
                    }
                    customEventHandler.dispatchEvent('stateChanged', { type: 'save' });
                }
                if (selectedToolRef.current.length > 1 && selectedToolRef.current.includes(sourceId)) {
                    customEventHandler.dispatchEvent('multiSelectMoveEnd', { sourceId: sourceId });
                }
            }
            dragStateRef.current = null;
        };
        window.addEventListener('mousemove', onWindowMouseMove);
        window.addEventListener('mouseup', onWindowMouseUp);
        return function () {
            window.removeEventListener('mousemove', onWindowMouseMove);
            window.removeEventListener('mouseup', onWindowMouseUp);
        };
    }, [zoomLevel]);
    // ---- Multi-select follower (component-level: one listener for the whole
    // annotations array, since RaphaelCanvas owns all of them in one instance) ----
    useEffect(function () {
        var followerSnapshot = [];
        var followerSourceId = null;
        var gestureId = 0;
        // `multiSelectMove`'s deltaX/deltaY are already cumulative-since-gesture-start
        // (each call re-applies the total delta to the ORIGINAL snapshot, same model
        // as the central engine — see MultiSelectMove.md), so tracking only the LATEST
        // call's delta is enough. Needed because `onEnd` only receives `{ sourceId }`
        // — no delta — and can't derive the final position by reading
        // `annotationsRef.current`: for a single-shot gesture (Start+Move+End all
        // dispatched synchronously in one tick), React hasn't necessarily flushed the
        // last onMove's setAnnotations by the time onEnd runs, so that ref can be
        // stale. Computing the final value from the ORIGINAL snapshot + last delta
        // instead sidesteps that timing entirely.
        var lastDeltaX = 0;
        var lastDeltaY = 0;
        var onStart = function (_a) {
            var sourceId = _a.sourceId;
            gestureId += 1;
            followerSourceId = sourceId;
            followerSnapshot = [];
            lastDeltaX = 0;
            lastDeltaY = 0;
            selectedToolRef.current.forEach(function (toolId) {
                var _a;
                if (!toolId.startsWith('annotations_') || toolId === sourceId)
                    return;
                var idx = Number(toolId.split('_')[1]);
                var ann = annotationsRef.current[idx];
                if (!ann || ((_a = ann.data.lock) === null || _a === void 0 ? void 0 : _a.positioning))
                    return;
                if (ann.type === 'line' && ann.data.points) {
                    followerSnapshot.push({ idx: idx, points: ann.data.points.map(function (p) { return (__assign({}, p)); }) });
                }
                else if (ann.data.center) {
                    followerSnapshot.push({ idx: idx, center: __assign({}, ann.data.center) });
                }
            });
        };
        var onMove = function (_a) {
            var sourceId = _a.sourceId, deltaX = _a.deltaX, deltaY = _a.deltaY;
            if (sourceId !== followerSourceId || followerSnapshot.length === 0)
                return;
            // Snapshot the reference NOW, synchronously. `followerSnapshot` is a shared
            // mutable closure variable that `onEnd`'s `hardReset()` reassigns to `[]` —
            // for a real mouse drag there are many render passes between each `onMove`
            // and the eventual `onEnd`, so React has already flushed this updater by
            // then. But a single-shot programmatic gesture (Start+Move+End dispatched
            // synchronously in one tick, e.g. the context-menu Move action) gives React
            // no chance to run this updater before `hardReset()` fires: the updater is
            // invoked later, during the render pass, by which point `followerSnapshot`
            // has already been reassigned to `[]` and this forEach would silently
            // iterate zero items, leaving every follower annotation unmoved. Capturing
            // the array reference in a local const closes over the ORIGINAL populated
            // array regardless of what the outer variable gets reassigned to later.
            var snapshot = followerSnapshot;
            lastDeltaX = deltaX;
            lastDeltaY = deltaY;
            setAnnotations(function (prev) {
                var next = __spreadArray([], prev, true);
                snapshot.forEach(function (s) {
                    var ann = next[s.idx];
                    if (!ann)
                        return;
                    if (s.points) {
                        next[s.idx] = __assign(__assign({}, ann), { data: __assign(__assign({}, ann.data), { points: s.points.map(function (p) { return ({ x: p.x + deltaX, y: p.y + deltaY }); }) }) });
                    }
                    else if (s.center) {
                        next[s.idx] = __assign(__assign({}, ann), { data: __assign(__assign({}, ann.data), { center: { x: s.center.x + deltaX, y: s.center.y + deltaY } }) });
                    }
                });
                return next;
            });
        };
        var onEnd = function (_a) {
            var sourceId = _a.sourceId;
            if (sourceId === followerSourceId && followerSnapshot.length > 0) {
                // Compute old/new directly from the original snapshot + the last applied
                // delta, NOT from `annotationsRef.current` — for a single-shot gesture
                // (Start+Move+End dispatched synchronously) React may not have flushed
                // onMove's setAnnotations yet, so the ref can still reflect the
                // pre-move value here, producing a bogus old===new no-op undo entry.
                var entries = followerSnapshot
                    .map(function (s) {
                    var ann = annotationsRef.current[s.idx];
                    if (!ann)
                        return null;
                    var oldData = s.points ? { points: s.points } : { center: s.center };
                    var newData = s.points
                        ? { points: s.points.map(function (p) { return ({ x: p.x + lastDeltaX, y: p.y + lastDeltaY }); }) }
                        : { center: { x: s.center.x + lastDeltaX, y: s.center.y + lastDeltaY } };
                    return {
                        type: 'annotations',
                        id: String(s.idx),
                        propertyName: 'completeData',
                        old: JSON.stringify(__assign(__assign({}, ann), { data: __assign(__assign({}, ann.data), oldData) })),
                        new: JSON.stringify(__assign(__assign({}, ann), { data: __assign(__assign({}, ann.data), newData) })),
                    };
                })
                    .filter(Boolean);
                if (entries.length > 0 && !registerMultiSelectSaveEntries(sourceId, entries)) {
                    saveState(entries);
                }
            }
            hardReset();
        };
        var hardReset = function () {
            followerSnapshot = [];
            followerSourceId = null;
        };
        // Fallback ONLY for a gesture that ends without 'multiSelectMoveEnd' ever firing. Deferred via
        // setTimeout(0) — not called synchronously — because this listener is registered once here at
        // mount (very early), while the leader widget's own mouseup->onUp listener (which dispatches
        // 'multiSelectMoveEnd') is registered fresh at that drag's mousedown, much later. Same-target
        // DOM listeners fire in registration order, so a synchronous reset here would run BEFORE the
        // leader's own onUp dispatches 'multiSelectMoveEnd', wiping followerSnapshot before onEnd ever
        // sees it and silently dropping every annotation follower save. Mirrors the identical fix in
        // useMultiSelectDrag.ts's deferredHardResetFallback (see its comment for the full rationale),
        // including the gestureId guard against wiping a brand-new gesture that starts before this
        // deferred callback runs.
        var deferredHardReset = function () {
            var staleGestureId = gestureId;
            setTimeout(function () {
                if (gestureId === staleGestureId)
                    hardReset();
            }, 0);
        };
        customEventHandler.addEventListener('multiSelectMoveStart', onStart);
        customEventHandler.addEventListener('multiSelectMove', onMove);
        customEventHandler.addEventListener('multiSelectMoveEnd', onEnd);
        window.addEventListener('mouseup', deferredHardReset);
        window.addEventListener('touchend', deferredHardReset);
        return function () {
            customEventHandler.removeEventListener('multiSelectMoveStart', onStart);
            customEventHandler.removeEventListener('multiSelectMove', onMove);
            customEventHandler.removeEventListener('multiSelectMoveEnd', onEnd);
            window.removeEventListener('mouseup', deferredHardReset);
            window.removeEventListener('touchend', deferredHardReset);
        };
    }, []);
    // Shared annotation move function — used by both arrow keys and context menu
    // skipSave: true during key hold (saves on keyup instead)
    var moveStartRef = useRef(null);
    var moveAnnotation = function (idx, dx, dy, direction, skipSave) {
        var _a, _b, _c, _d, _e;
        var ann = annotationsRef.current[idx];
        if (!ann || ((_a = ann.data.lock) === null || _a === void 0 ? void 0 : _a.positioning))
            return;
        // Capture start state on first move of a key-hold sequence
        if (skipSave && !moveStartRef.current) {
            moveStartRef.current = { idx: idx, old: JSON.stringify(ann) };
        }
        var newAnn;
        if (ann.type === 'line' && ann.data.points) {
            var newPts = ann.data.points.map(function (p) { return ({ x: p.x + dx, y: p.y + dy }); });
            newAnn = __assign(__assign({}, ann), { data: __assign(__assign({}, ann.data), { points: newPts }) });
        }
        else if (ann.data.center) {
            var newCenter = { x: ann.data.center.x + dx, y: ann.data.center.y + dy };
            newAnn = __assign(__assign({}, ann), { data: __assign(__assign({}, ann.data), { center: newCenter }) });
        }
        if (!newAnn)
            return;
        setAnnotations(function (prev) {
            var next = __spreadArray([], prev, true);
            next[idx] = newAnn;
            return next;
        });
        // For context menu moves: save + announce immediately
        if (!skipSave) {
            saveState({
                type: 'annotations',
                id: String(idx),
                propertyName: 'completeData',
                old: JSON.stringify(ann),
                new: JSON.stringify(newAnn),
            });
            customEventHandler.dispatchEvent('stateChanged', { type: 'save' });
            if (direction) {
                var dirMap = {
                    moveUp: 'up', moveDown: 'down', moveLeft: 'left', moveRight: 'right',
                };
                var pos = newAnn.type === 'line' && newAnn.data.points
                    ? { x: Math.round((newAnn.data.points[0].x + newAnn.data.points[1].x) / 2), y: Math.round((newAnn.data.points[0].y + newAnn.data.points[1].y) / 2) }
                    : { x: Math.round((_c = (_b = newAnn.data.center) === null || _b === void 0 ? void 0 : _b.x) !== null && _c !== void 0 ? _c : 0), y: Math.round((_e = (_d = newAnn.data.center) === null || _d === void 0 ? void 0 : _d.y) !== null && _e !== void 0 ? _e : 0) };
                announce(t('text.annotationMoved', { name: ann.type, direction: dirMap[direction] || direction, x: pos.x, y: pos.y }));
            }
        }
    };
    // Finalize move on keyup — save single undo entry + announce final position
    var finalizeMoveAnnotation = function () {
        var _a, _b, _c, _d;
        if (!moveStartRef.current)
            return;
        var _e = moveStartRef.current, idx = _e.idx, old = _e.old;
        var ann = annotationsRef.current[idx];
        if (ann) {
            saveState({
                type: 'annotations',
                id: String(idx),
                propertyName: 'completeData',
                old: old,
                new: JSON.stringify(ann),
            });
            customEventHandler.dispatchEvent('stateChanged', { type: 'save' });
            var pos = ann.type === 'line' && ann.data.points
                ? { x: Math.round((ann.data.points[0].x + ann.data.points[1].x) / 2), y: Math.round((ann.data.points[0].y + ann.data.points[1].y) / 2) }
                : { x: Math.round((_b = (_a = ann.data.center) === null || _a === void 0 ? void 0 : _a.x) !== null && _b !== void 0 ? _b : 0), y: Math.round((_d = (_c = ann.data.center) === null || _c === void 0 ? void 0 : _c.y) !== null && _d !== void 0 ? _d : 0) };
            announce(t('text.annotationMoved', { name: ann.type, direction: '', x: pos.x, y: pos.y }));
        }
        moveStartRef.current = null;
    };
    // Listen for annotation move events from context menu
    useEffect(function () {
        var onAnnotationMove = function (data) {
            moveAnnotation(data.index, data.dx, data.dy, data.direction);
        };
        customEventHandler.addEventListener('annotationMove', onAnnotationMove);
        return function () { return customEventHandler.removeEventListener('annotationMove', onAnnotationMove); };
    }, []);
    useEffect(function () {
        var onColourChange = function (data) {
            var selected = selectedToolRef.current;
            selected.forEach(function (tool) {
                var _a;
                if (!tool.startsWith('annotations_'))
                    return;
                var idx = Number(tool.split('_')[1]);
                var ann = annotationsRef.current[idx];
                if (!ann || ((_a = ann.data.lock) === null || _a === void 0 ? void 0 : _a.editing))
                    return;
                var newAnn = __assign(__assign({}, ann), { data: __assign(__assign({}, ann.data), { color: data.color }) });
                saveState({
                    type: 'annotations',
                    id: String(idx),
                    propertyName: 'completeData',
                    old: JSON.stringify(ann),
                    new: JSON.stringify(newAnn),
                });
                setAnnotations(function (prev) {
                    var next = __spreadArray([], prev, true);
                    next[idx] = newAnn;
                    return next;
                });
                customEventHandler.dispatchEvent('stateChanged', { type: 'save' });
            });
        };
        customEventHandler.addEventListener('colourChange', onColourChange);
        return function () { return customEventHandler.removeEventListener('colourChange', onColourChange); };
    }, []);
    // Initialize Raphael only once
    useEffect(function () {
        if (containerRef.current && !paperRef.current) {
            paperRef.current = Raphael(containerRef.current, canvasWidth, canvasHeight);
            // Prevent clipping of rotated lines/shapes that extend beyond the SVG viewport
            var svgEl = containerRef.current.querySelector('svg');
            if (svgEl) {
                svgEl.style.overflow = 'visible';
            }
        }
        return function () {
            var _a, _b;
            (_a = paperRef.current) === null || _a === void 0 ? void 0 : _a.clear();
            (_b = paperRef.current) === null || _b === void 0 ? void 0 : _b.remove();
            paperRef.current = null;
        };
    }, [canvasWidth, canvasHeight, zoomLevel]);
    // Draw annotations whenever they change
    useEffect(function () {
        var paper = paperRef.current;
        if (!paper)
            return;
        // Clear previous drawings
        paper.clear();
        var cleanUp = annotations.map(function (shape, _ind) {
            var _a, _b, _c, _d, _e;
            var element;
            var patch = undefined;
            var _arr1 = undefined;
            var _arr2 = undefined;
            var _f = ((_a = shape.data) === null || _a === void 0 ? void 0 : _a.dimensions) || {}, _g = _f.width, width = _g === void 0 ? 0 : _g, _h = _f.height, height = _h === void 0 ? 0 : _h;
            var _j = ((_b = shape.data) === null || _b === void 0 ? void 0 : _b.center) || {}, _k = _j.x, xPos = _k === void 0 ? 0 : _k, _l = _j.y, yPos = _l === void 0 ? 0 : _l;
            var _m = shape.data || {}, color = _m.color, style = _m.style, thickness = _m.thickness, points = _m.points, arrowheadStyle = _m.arrowheadStyle;
            var x = xPos - width / 2, y = yPos - height / 2;
            switch (shape.type) {
                case 'rectangle':
                    element = paper.rect(x, y, width, height);
                    element.rotate(((_c = shape.data) === null || _c === void 0 ? void 0 : _c.rotation) || 0);
                    break;
                case 'ellipse':
                    element = paper.ellipse(xPos, yPos, width / 2, height / 2);
                    element.rotate(((_d = shape.data) === null || _d === void 0 ? void 0 : _d.rotation) || 0);
                    break;
                case 'line':
                    // Keep sub-pixel precision — rounding to integers bakes in a 1px misalignment
                    // between endpoints which creates a visible jag, especially for near-vertical lines.
                    var _pt = points === null || points === void 0 ? void 0 : points.map(function (_a) {
                        var x = _a.x, y = _a.y;
                        return ({ x: String(x), y: String(y) });
                    });
                    var angle = Math.atan2(Number(_pt === null || _pt === void 0 ? void 0 : _pt[1].y) - Number(_pt === null || _pt === void 0 ? void 0 : _pt[0].y), Number(_pt === null || _pt === void 0 ? void 0 : _pt[1].x) - Number(_pt === null || _pt === void 0 ? void 0 : _pt[0].x));
                    // Only inset endpoints when arrowheads are present (to avoid overlap)
                    var hasArrows = (arrowheadStyle === null || arrowheadStyle === void 0 ? void 0 : arrowheadStyle.start) === 'arrow' || (arrowheadStyle === null || arrowheadStyle === void 0 ? void 0 : arrowheadStyle.end) === 'arrow';
                    var inset = hasArrows ? thickness / 2 : 0;
                    var xDiff = Math.cos(angle) * inset;
                    var yDiff = Math.sin(angle) * inset;
                    var startX = Number(_pt === null || _pt === void 0 ? void 0 : _pt[0].x) + xDiff;
                    var startY = Number(_pt === null || _pt === void 0 ? void 0 : _pt[0].y) + yDiff;
                    var endX = Number(_pt === null || _pt === void 0 ? void 0 : _pt[1].x) - xDiff;
                    var endY = Number(_pt === null || _pt === void 0 ? void 0 : _pt[1].y) - yDiff;
                    element = paper.path("M ".concat(startX, " ").concat(startY, " L ").concat(endX, " ").concat(endY));
                    // Rotate around the midpoint of the original (non-inset) points
                    // so thickness changes expand from center, not from one side
                    var lineRotation = ((_e = shape.data) === null || _e === void 0 ? void 0 : _e.rotation) || 0;
                    var lineCx = (Number(_pt === null || _pt === void 0 ? void 0 : _pt[0].x) + Number(_pt === null || _pt === void 0 ? void 0 : _pt[1].x)) / 2;
                    var lineCy = (Number(_pt === null || _pt === void 0 ? void 0 : _pt[0].y) + Number(_pt === null || _pt === void 0 ? void 0 : _pt[1].y)) / 2;
                    if (lineRotation) {
                        element.rotate(lineRotation, lineCx, lineCy);
                    }
                    var patchSize = 2 * (24 + thickness);
                    // draw a transparent patch over the line for events
                    var patchX = Math.min(startX, endX) - patchSize / 2;
                    var patchY = Math.min(startY, endY) - patchSize / 2;
                    var patchW = Math.abs(endX - startX) + patchSize;
                    var patchH = Math.abs(endY - startY) + patchSize;
                    patch = paper
                        .rect(patchX, patchY, patchW, patchH)
                        .attr({ fill: 'transparent', cursor: 'pointer' });
                    patch.rotate((angle * 180) / Math.PI + lineRotation, lineCx, lineCy);
                    if ((arrowheadStyle === null || arrowheadStyle === void 0 ? void 0 : arrowheadStyle.end) === 'arrow') {
                        _arr1 = addArrowHead(paper, startX, startY, endX, endY, 24 + thickness).attr({
                            fill: color,
                            cursor: 'pointer',
                            stroke: color,
                        });
                        if (lineRotation)
                            _arr1.rotate(lineRotation, lineCx, lineCy);
                        _arr1.click(function (e) { return handleAnnotationClick(e, _ind); });
                    }
                    if ((arrowheadStyle === null || arrowheadStyle === void 0 ? void 0 : arrowheadStyle.start) === 'arrow') {
                        _arr2 = addArrowHead(paper, endX, endY, startX, startY, 24 + thickness).attr({
                            fill: color,
                            cursor: 'pointer',
                            stroke: color,
                        });
                        if (lineRotation)
                            _arr2.rotate(lineRotation, lineCx, lineCy);
                        _arr2.click(function (e) { return handleAnnotationClick(e, _ind); });
                    }
                    break;
                default:
                    console.warn('Unknown shape type:', shape.type);
                    return;
            }
            element.attr({
                fill: style == 'fill' ? color : 'transparent',
                stroke: color,
                'stroke-width': thickness,
                cursor: 'pointer',
            });
            if (patch) {
                patch.attr({
                    fill: 'transparent',
                    stroke: 'transparent',
                    cursor: 'pointer',
                });
                patch.click(function (e) { return handleAnnotationClick(e, _ind); });
                patch.mousedown(function (e) { return handleAnnotationMouseDown(((e === null || e === void 0 ? void 0 : e.originalEvent) || e), _ind); });
            }
            element.click(function (e) { return handleAnnotationClick(e, _ind); });
            element.mousedown(function (e) { return handleAnnotationMouseDown(((e === null || e === void 0 ? void 0 : e.originalEvent) || e), _ind); });
            // Keyboard accessibility: make annotation focusable via Tab
            var focusTarget = (patch ? patch.node : element.node);
            focusTarget.setAttribute('tabindex', '0');
            // role="img" + aria-label is the canonical pattern for a focusable
            // visual shape. role="application" silenced the aria-label on JAWS /
            // VoiceOver because it told the screen reader to suppress its normal
            // label announcement in favour of "widget" semantics. The keydown
            // listener below is attached directly to this element, so keyboard
            // handling still works without forms-mode.
            focusTarget.setAttribute('role', 'img');
            focusTarget.setAttribute('aria-roledescription', shape.type);
            // Build type name including arrowhead info for lines
            var typeName = shape.type;
            if (shape.type === 'line' && shape.data.arrowheadStyle) {
                var _o = shape.data.arrowheadStyle, start = _o.start, end = _o.end;
                if (start === 'arrow' && end === 'arrow') {
                    typeName = t('text.doubleArrowLine');
                }
                else if (start === 'arrow' || end === 'arrow') {
                    typeName = t('text.arrowLine');
                }
            }
            focusTarget.setAttribute('aria-label', t('text.annotationAriaLabel', { type: typeName, index: _ind + 1 }));
            focusTarget.setAttribute('data-manipulative-id', "annotations_".concat(_ind));
            // Clean up duplicate tracking (announcement handled by assertive announcer in GameController)
            var annotationId = "annotations_".concat(_ind);
            if (pendingDuplicateIds.has(annotationId)) {
                pendingDuplicateIds.delete(annotationId);
            }
            // Focus outline handled by CSS :focus-visible (do not suppress inline)
            focusTarget.addEventListener('focus', function () {
                var already = selectedToolRef.current.includes("annotations_".concat(_ind));
                // Don't collapse a multi-select gesture: when the user is shift /
                // ctrl / meta-clicking, the click handler will assemble the new
                // selection — skip the single-select override here.
                if (!already && !isMultiSelectModifierHeld()) {
                    setSelectedTool(["annotations_".concat(_ind)]);
                }
                focusTarget.setAttribute('data-focused', 'true');
                // VoiceOver platform-scrolls the canvas to the wrong position when
                // focusing SVG elements inside a CSS-transformed container. The scroll
                // event DOES fire (unlike with preventScroll), so we can catch it and
                // restore the saved position immediately.
                var scrollEl = document.querySelector('.whiteboardScrollbar .customScrollbarContent');
                if (scrollEl) {
                    var savedLeft_1 = scrollEl.scrollLeft;
                    var savedTop_1 = scrollEl.scrollTop;
                    var restore_1 = function () {
                        scrollEl.scrollLeft = savedLeft_1;
                        scrollEl.scrollTop = savedTop_1;
                    };
                    scrollEl.addEventListener('scroll', restore_1, { once: true });
                    // Clean up if VoiceOver never scrolls (mouse user, no VO scroll)
                    setTimeout(function () { return scrollEl.removeEventListener('scroll', restore_1); }, 500);
                }
            });
            // Arrow keys: move annotation (prevents native scroll)
            // skipSave=true during hold — saves single undo entry on keyup
            focusTarget.addEventListener('keydown', (function (e) {
                if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                    e.preventDefault();
                    e.stopPropagation();
                    var STEP = e.shiftKey ? 50 : 10;
                    var d = {
                        ArrowUp: { x: 0, y: -STEP },
                        ArrowDown: { x: 0, y: STEP },
                        ArrowLeft: { x: -STEP, y: 0 },
                        ArrowRight: { x: STEP, y: 0 },
                    };
                    moveAnnotation(_ind, d[e.key].x, d[e.key].y, undefined, true);
                }
            }));
            focusTarget.addEventListener('keyup', (function (e) {
                if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                    finalizeMoveAnnotation();
                }
            }));
            focusTarget.addEventListener('blur', function () {
                focusTarget.removeAttribute('data-focused');
            });
            return function () {
                element.remove();
                patch === null || patch === void 0 ? void 0 : patch.remove();
                _arr1 === null || _arr1 === void 0 ? void 0 : _arr1.remove();
                _arr2 === null || _arr2 === void 0 ? void 0 : _arr2.remove();
            };
        });
        // Force repaint after drawing completes to prevent ghost trails on some machines
        requestAnimationFrame(function () {
            var _a;
            var el = containerRef.current;
            if (el) {
                el.style.transform = 'translateZ(0)';
                void el.offsetHeight;
                el.style.transform = '';
            }
            // Refocus the selected annotation after Raphael redraw (SVG elements are recreated)
            // Refocus annotation SVG only if focus isn't already on a meaningful element
            // (e.g., don't steal focus from the rotate handle in the resizer)
            var activeEl = document.activeElement;
            var isOnResizer = activeEl === null || activeEl === void 0 ? void 0 : activeEl.closest('.resizeHolder');
            var isOnContextMenu = activeEl === null || activeEl === void 0 ? void 0 : activeEl.closest('.shape-context-menu');
            if (!isOnResizer && !isOnContextMenu) {
                var selectedAnn = selectedToolRef.current.find(function (s) { return s.startsWith('annotations_'); });
                if (selectedAnn) {
                    var target = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.querySelector("[data-manipulative-id=\"".concat(selectedAnn, "\"]"));
                    if (target && document.activeElement !== target) {
                        target.focus({ preventScroll: true });
                    }
                }
            }
        });
        return function () {
            paper.clear();
            cleanUp.forEach(function (item) { return item === null || item === void 0 ? void 0 : item(); });
        };
    }, [annotations, zoomLevel, dispatch]);
    useEffect(function () {
        var selectedAnn = selectedTool.find(function (s) { return s.startsWith('annotations_'); });
        if (!selectedAnn || !containerRef.current)
            return;
        var target = containerRef.current.querySelector("[data-manipulative-id=\"".concat(selectedAnn, "\"]"));
        if (target && document.activeElement !== target) {
            target.focus({ preventScroll: true });
        }
    }, [selectedTool]);
    useEffect(function () {
        var onClick = function (data) {
            switch (data.id) {
                case 'rectangle':
                case 'ellipse':
                    selectedTool.forEach(function (_select) {
                        if (_select.startsWith('annotation')) {
                            var pos_1 = Number(_select.split('_')[1]);
                            setAnnotations(function (text) {
                                var _bool = !(text[pos_1].data.active || false);
                                text[pos_1].data.active = _bool;
                                text[pos_1].data.style = _bool ? 'outline' : 'fill';
                                return __spreadArray([], text, true);
                            });
                        }
                    });
                    break;
            }
            if (data.id.startsWith('highlighterCollapsible')) {
                var thickness_1 = Number(data.id.split('_')[1]);
                selectedTool.forEach(function (_select) {
                    if (_select.startsWith('annotation')) {
                        var pos_2 = Number(_select.split('_')[1]);
                        setAnnotations(function (text) {
                            text[pos_2].data.thickness = thickness_1 * 4 + 3;
                            return __spreadArray([], text, true);
                        });
                    }
                });
            }
        };
        customEventHandler.addEventListener('bottomPanel', onClick);
        return function () {
            customEventHandler.removeEventListener('bottomPanel', onClick);
        };
    }, [selectedTool]);
    function addArrowHead(paper, x1, y1, x2, y2, size) {
        if (size === void 0) { size = 30; }
        var angle = Math.atan2(y2 - y1, x2 - x1);
        var arrowAngle = Math.PI / 6; // angle between arrow sides
        var arrowLength = size;
        var xA = x2 - arrowLength * Math.cos(angle - arrowAngle);
        var yA = y2 - arrowLength * Math.sin(angle - arrowAngle);
        var xB = x2 - arrowLength * Math.cos(angle + arrowAngle);
        var yB = y2 - arrowLength * Math.sin(angle + arrowAngle);
        var xC = x2 + (arrowLength / 2) * Math.cos(angle);
        var yC = y2 + (arrowLength / 2) * Math.sin(angle);
        return paper.path("M".concat(xA, ",").concat(yA, " L").concat(xC, ",").concat(yC, " L").concat(xB, ",").concat(yB, " L").concat(xA, ",").concat(yA));
    }
    // Keyboard handler for annotation context menu (Enter/Space)
    // Uses document listener because SVG elements inside pointer-events:none container
    // don't bubble React events properly
    useEffect(function () {
        var handleKeyDown = function (e) {
            var _a;
            var target = e.target;
            // Only handle if focus is on an annotation SVG element
            if (!((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(target)))
                return;
            if (target.getAttribute('tabindex') !== '0')
                return;
            // Context menu
            if (e.key === 'Enter' ||
                e.key === ' ' ||
                e.key === 'ContextMenu' ||
                (e.shiftKey && e.key === 'F10')) {
                e.preventDefault();
                e.stopPropagation();
                var annId = selectedToolRef.current.find(function (s) { return s.startsWith('annotations_'); });
                if (annId) {
                    var rect = target.getBoundingClientRect();
                    setContextMenu({
                        x: rect.right + 8,
                        y: rect.top,
                        manipulativeId: annId,
                    });
                }
                return;
            }
        };
        // iPad VoiceOver double-tap fires a synthetic click (detail === 0).
        // pointer-events:none on .raphealContainer blocks normal click delivery,
        // but VoiceOver routes clicks to the focused element directly, so a
        // document-level capture listener catches them.
        var handleClick = function (e) {
            var _a;
            if (e.detail !== 0)
                return; // Only synthetic (VoiceOver) clicks
            var target = e.target;
            if (!((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(target)))
                return;
            var annId = target.getAttribute('data-manipulative-id');
            if (!annId || !annId.startsWith('annotations_'))
                return;
            e.preventDefault();
            e.stopPropagation();
            var rect = target.getBoundingClientRect();
            setContextMenu({
                x: rect.right + 8,
                y: rect.top,
                manipulativeId: annId,
            });
        };
        document.addEventListener('keydown', handleKeyDown, true);
        document.addEventListener('click', handleClick, true);
        return function () {
            document.removeEventListener('keydown', handleKeyDown, true);
            document.removeEventListener('click', handleClick, true);
        };
    }, [setContextMenu]);
    return (_jsx("div", { className: "raphealContainer", role: "region", "aria-label": "Vector annotations area", children: _jsx("div", { ref: containerRef, style: {
                width: canvasWidth,
                height: canvasHeight,
            }, className: "raphaelCanvas", role: "group", "aria-label": t('text.annotationsGroup') }) }));
};
export default RaphaelCanvas;
